/**
 * Repaints live canvases while their video layers play.
 *
 * Fabric only repaints when told to, and a playing <video> changes pixels
 * without any Fabric event. One module-level, ref-counted rAF loop calls
 * requestRenderAll() on each canvas that has a playing layer, and stops
 * itself when the count reaches zero.
 *
 * Rendering fires no Fabric object events, so the pump carries no history
 * or autosave risk — provided it NEVER calls obj.set() or mutates an object.
 * It only touches the canvas.
 */

import type { fabric } from 'fabric';

const pumping = new Map<object, fabric.Canvas>();
let frame = 0;

function tick(): void {
  frame = 0;
  if (!pumping.size) return;
  for (const canvas of new Set(pumping.values())) canvas.requestRenderAll();
  frame = requestAnimationFrame(tick);
}

/** Keep `canvas` repainting while `obj` plays. Idempotent per object. */
export function registerPumping(canvas: fabric.Canvas, obj: object): void {
  pumping.set(obj, canvas);
  if (!frame) frame = requestAnimationFrame(tick);
}

export function unregisterPumping(obj: object): void {
  pumping.delete(obj);
  if (!pumping.size && frame) {
    cancelAnimationFrame(frame);
    frame = 0;
  }
}

/** True while any layer is being pumped (for tests and diagnostics). */
export function isPumping(obj?: object): boolean {
  return obj ? pumping.has(obj) : pumping.size > 0;
}
